import React, { useEffect, useRef, useState, useCallback } from "react";
import logo from "@/assets/252dp_logo.gif";
import L from "leaflet";
import { useWindyMapStore } from "@/stores/windy-map-store";
import WeatherMenu from "./weather-menu";

// Types
interface WindyAPI {
  map: L.Map;
  store: any;
}

interface WindyMapOptions {
  key: string;
  verbose: boolean;
  lat: number;
  lon: number;
  zoom: number;
  useCache: boolean;
  cache: boolean;
  overlay: string;
  useGeolocation: boolean;
  particleAnimate: boolean;
}

// Constants
const WINDY_SCRIPT_SRC = "https://api.windy.com/assets/map-forecast/libBoot.js";
const WINDY_SCRIPT_ID = "windyScript";

const DEFAULT_MAP_OPTIONS: WindyMapOptions = {
  key: "SScb3SayNKHjlCCiObAZHdxolLRzzQcy", // Add your Windy API key here
  verbose: true,
  lat: 47.2, // Mongolia center
  lon: 104.06,
  zoom: 6,
  useCache: true,
  cache: true,
  overlay: "wind",
  useGeolocation: false,
  particleAnimate: true,
};

const WeatherMap: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const scriptLoadedRef = useRef(false);
  const mapInitializedRef = useRef(false);

  // Store actions
  const { setWindyMap, setLayer, setMapStore } = useWindyMapStore.getState();

  // Map event handlers
  const handleMapMoveEnd = useCallback((map: L.Map) => {
    const center = map.getCenter();
    console.log(
      "Газрын зураг төв:",
      center.lat.toFixed(5),
      ",",
      center.lng.toFixed(5)
    );
  }, []);

  const handleParameterChange = useCallback((params: any) => {
    console.log("Тухайн үед идэвхтэй overlay:", params.overlay);
  }, []);

  // Initialize Windy API
  const initializeWindyAPI = useCallback((windyAPI: WindyAPI) => {
    const { map, store } = windyAPI;

    map.whenReady(() => {
      try {
        // Set up map event listeners
        map.on("moveend", () => handleMapMoveEnd(map));
        
        // Set up store event listeners
        store.on("parameter", handleParameterChange);

        // Enable scroll wheel zoom
        map.scrollWheelZoom.enable();

        // Update store with map instances
        setWindyMap(map);
        setMapStore(store);
        setLayer(L.layerGroup().addTo(map));

        setIsLoading(false);
        mapInitializedRef.current = true;
      } catch (err) {
        console.error("Error initializing Windy map:", err);
        setError("Газрын зургийг ачаалахад алдаа гарлаа");
        setIsLoading(false);
      }
    });
  }, [handleMapMoveEnd, handleParameterChange, setWindyMap, setMapStore, setLayer]);

  // Load Windy script
  const loadWindyScript = useCallback(() => {
    if (scriptLoadedRef.current || document.getElementById(WINDY_SCRIPT_ID)) {
      return;
    }

    const script = document.createElement("script");
    script.id = WINDY_SCRIPT_ID;
    script.src = WINDY_SCRIPT_SRC;
    script.async = true;
    
    script.onload = () => {
      try {
        // @ts-ignore - Windy global function
        if (typeof windyInit === "undefined") {
          throw new Error("Windy API not loaded properly");
        }

        // @ts-ignore
        windyInit(DEFAULT_MAP_OPTIONS, initializeWindyAPI);
        scriptLoadedRef.current = true;
      } catch (err) {
        console.error("Error loading Windy script:", err);
        setError("Windy API ачаалахад алдаа гарлаа");
        setIsLoading(false);
      }
    };

    script.onerror = () => {
      console.error("Failed to load Windy script");
      setError("Windy script ачаалахад алдаа гарлаа");
      setIsLoading(false);
    };

    document.body.appendChild(script);
  }, [initializeWindyAPI]);

  // Initialize on mount
  useEffect(() => {
    loadWindyScript();

    // Cleanup function
    return () => {
      // Clean up map if it exists
      const currentMap = useWindyMapStore.getState().windyMap;
      if (currentMap && mapInitializedRef.current) {
        currentMap.remove();
        mapInitializedRef.current = false;
      }
    };
  }, [loadWindyScript]);

  // Loading component
  const LoadingOverlay = () => (
    <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-10">
      <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 text-center">
        <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-3" />
        <div className="text-white text-sm">Газрын зураг ачаалж байна...</div>
      </div>
    </div>
  );

  // Error component
  const ErrorOverlay = () => (
    <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-10">
      <div className="bg-red-500/20 backdrop-blur-sm rounded-lg p-6 text-center border border-red-500/30">
        <div className="text-red-400 text-lg mb-2">⚠️ Алдаа гарлаа</div>
        <div className="text-white text-sm mb-4">{error}</div>
        <button 
          onClick={() => {
            setError(null);
            setIsLoading(true);
            scriptLoadedRef.current = false;
            loadWindyScript();
          }}
          className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded text-white text-sm transition-colors"
        >
          Дахин оролдох
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex-1 flex relative">
      {/* Windy Map Container */}
      <div
        id="windy"
        className="w-full flex-1"
        style={{ 
          willChange: "transform", 
          transform: "translateZ(0)",
          minHeight: "400px" // Prevent layout shift
        }}
      />

      {/* Loading State */}
      {isLoading && <LoadingOverlay />}

      {/* Error State */}
      {error && <ErrorOverlay />}

      {/* Logo */}
      <div className="absolute -top-12 right-16 h-[200px] w-[200px] pointer-events-none">
        <img
          alt="252.mn лого"
          src={logo}
          className="w-full h-full object-contain"
          style={{ 
            filter: "drop-shadow(0 0 2px rgba(0,0,0,0.8))",
            imageRendering: "crisp-edges" // Better for GIF rendering
          }}
          loading="eager"
        />
      </div>

      {/* Weather Menu */}
      <div className="absolute bottom-14 right-10">
        <WeatherMenu />
      </div>
    </div>
  );
};

export default WeatherMap;